import { useState } from "react";
import { ChevronDown } from "lucide-react";
import AnimatedAccordion from "@/components/AnimatedAccordion";

/**
 * Site-wide responsive accordion.
 *
 * <768px (mobile):  flat list, shared `border-b` dividers between rows, no wrapper box.
 * ≥768px (md up):   rounded-xl bordered boxes with `bg-gray-50` fill, 12px gap between items.
 *
 * Each item owns its own open state → multiple items can be expanded simultaneously.
 *
 * Spacing is tuned so content padding reads as equal on all sides in both breakpoints.
 *
 * @param {{q:string,a:string|React.ReactNode}[]} items
 * @param {string} testIdPrefix  Prefix used to derive per-item test IDs (default "faq").
 * @param {string} className
 */
export default function ResponsiveAccordion({ items = [], testIdPrefix = "faq", className = "", headingLevel = "h4" }) {
  return (
    <div className={`md:space-y-3 ${className}`} data-testid={`${testIdPrefix}-list`}>
      {items.map((item, i) => (
        <ResponsiveAccordionItem
          key={item.q || i}
          question={item.q}
          answer={item.a}
          headingLevel={headingLevel}
          testId={`${testIdPrefix}-${(item.q || String(i)).slice(0, 20).replace(/\s+/g, "-").toLowerCase()}`}
        />
      ))}
    </div>
  );
}

function ResponsiveAccordionItem({ question, answer, testId, headingLevel = "h4" }) {
  const HeadingTag = headingLevel;
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-brand-light-grey md:border md:bg-gray-50 md:rounded-xl md:overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-3 md:px-6 md:py-4 text-left md:hover:bg-gray-100 transition-colors"
        data-testid={testId}
        aria-expanded={open}
      >
        <HeadingTag className="font-semibold text-sm text-brand-black pr-4">{question}</HeadingTag>
        <ChevronDown
          size={16}
          className={`flex-shrink-0 text-brand-grey transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        />
      </button>
      <AnimatedAccordion isOpen={open}>
        <div className="text-sm text-brand-grey leading-relaxed pb-4 pr-6 md:px-6 md:pt-1 md:pb-5">
          {answer}
        </div>
      </AnimatedAccordion>
    </div>
  );
}
